import React, { useMemo } from 'react';
import { Designer } from '@graphql/Designer/queries/availableDesigners.graphql';
import AvatarImage from '@components/shared/AvatarImage/AvatarImage';
import Carousel from 'react-multi-carousel';
import CTA from '@components/pages/InteriorDesigners/shared/buttons/CTA';
import { getResizedImageUrl } from '@lib/image';
import classNames from 'classnames';
import { useTheme } from '@components/theme';
import { useRouter } from 'next/router';
import DesignerGroupBadge from '@components/shared/Designers/components/DesignerGroupBadge/DesignerGroupBadge';
import designerGroups from '@constants/designerGroups';
import DesignerAvailableText from '@components/shared/DesignerAvailableText/DesignerAvailableText';
import { Room } from '@graphql/Room/queries/get-room.graphql';
import ReadMore from '../../../pages/InteriorDesigners/shared/ReadMoreContainer/ReadMoreContainer';
import LocationLink from '../LocationLinks/LocationLink';

import styles from './Card.module.scss';

interface ICardProps {
  cities?: string[];
  designer: Designer;
  CTAHandler(): void;
  room?: Room;
}

const Card = ({
  cities, designer, CTAHandler, room
}: ICardProps) => {
  const { designAesthetic } = designer;
  const topDesignAssets = useTopDesignAssets(designer);

  const theme = useTheme();
  const router = useRouter();

  const queryParams = router.query;
  // If we get a roomId, customer is trying to change an 11 star designer
  const profileUri = room?.id ? `${designer.profileUri}?roomId=${room.id}&fromChangeDesigner=true`
    : `${designer.profileUri}${queryParams.redirectToRoom ? `?redirectToRoom=${queryParams.redirectToRoom}` : ''}`;

  return (
    <div className={styles.Card} data-test="slug-designer-cards">
      <div className={styles.Header}>
        <a href={profileUri} className={styles.Avatar}>
          <AvatarImage
            alt={designer.displayName}
            imageUrl={designer.photo}
          />
        </a>
        <div className={styles.LocationStyle}>

          {!!designer.location && (
            <LocationLink
              cities={cities}
              location={designer.location}
            />
          )}

          <div className={styles.Available}>
            <DesignerAvailableText designer={designer} room={room} />
          </div>

          <DesignerGroupBadge
            designer={designer}
            forGroup={designerGroups.inPerson}
            classname={styles.BadgeWrapper}
          />

        </div>
      </div>

      <div className={styles.DesignerName}>
        <a href={profileUri}>
          {designer.firstName}
        </a>
        {designer.user?.pronouns && (
          <span className={styles.Pronouns}>{designer.user?.pronouns}</span>
        )}
      </div>

      { designAesthetic ? (
        <ReadMore
          className={classNames(theme.styles.Body, styles.Bio)}
          text={designAesthetic}
        />

      ) : (
        <div className={styles.Bio} />
      )}

      <Carousel
        draggable
        arrows
        className={styles.Carousel}
        dotListClass={styles.DotList}
        keyBoardControl
        slidesToSlide={1}
        renderDotsOutside
        responsive={carouselLayout()}
        showDots
      >
        {topDesignAssets.map((asset) => (
          <div style={{ width: '100%' }} key={asset.id}>
            <img
              src={asset.resizedUrl}
              alt={asset.assetType.title}
            />
          </div>
        ))}
        <CTABanner
          name={designer.firstName}
          href={profileUri}
        />
      </Carousel>

      <CTA
        testSelector={'slug-designer-cta'}
        type={'Outline'}
        className={styles.CTA}
        handler={CTAHandler}
      >

        Design with {designer.firstName}
      </CTA>
    </div>
  );
};

export function CTABanner({ name, href } : {name: string; href: string}) {
  return (
    <div className={styles.CTABanner}>
      <a href={href}>
        See more designs by<br />{name}
      </a>
    </div>
  );
}

export function useTopDesignAssets(designer: Designer) {
  return useMemo(() => {
    return (designer.topDesigns || [])
      .filter((design: any) => !!design.mainBoardAsset?.asset)
      .map((design: any) => {
        const { asset } = design.mainBoardAsset;

        return {
          ...asset,
          resizedUrl: getResizedImageUrl({
            url: asset.url,
            width: 600
          })
        };
      });
  }, [designer]);
}

export function carouselLayout() {
  return {
    desktop: {
      breakpoint: {
        max: 3000,
        min: 1024,
      },
      items: 1,
    },
    mobile: {
      breakpoint: {
        max: 464,
        min: 0,
      },
      items: 1,
    },
    tablet: {
      breakpoint: {
        max: 1024,
        min: 464,
      },
      items: 1,
    },
  };
}

export default Card;
